/**
 * Mock data for the Constrained Forecast Updated (15-Month) screen.
 * Shared between view and edit screens.
 */

import {
  singleConceptInterface,
  singleContractInterface,
  notesInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';

// ---------------------------------------------------------------------------
// 15-month helper
// ---------------------------------------------------------------------------

export interface PlanningMonth {
  label: string;
  dateString: string;
  /** true for months 13-15 (the 3 additional extended forecast months) */
  isExtended: boolean;
}

export const getFutureMonths15 = (): PlanningMonth[] => {
  const arr: PlanningMonth[] = [];
  const dt = new Date();
  const startMonth = dt.getMonth();
  const startYear = dt.getFullYear();
  const monthShortHand = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  for (let i = 0; i < 15; i++) {
    const month = (startMonth + i + 1) % 12;
    const year = startYear + Math.floor((startMonth + i + 1) / 12);
    arr.push({
      label: `${monthShortHand[month]}-${year}`,
      dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      isExtended: i >= 12,
    });
  }

  return arr;
};

// Pre-compute months for data generation
const MONTHS_15 = getFutureMonths15();

// ---------------------------------------------------------------------------
// Concept details — matches singleConceptInterface
// ---------------------------------------------------------------------------

export const mockConceptDetails: singleConceptInterface = {
  concept: 'Lisinopril 20mg Tablet',
  gfc: 54321,
  generic_rxcui: '29046',
  therapeutic_category: 'Cardiovascular',
  pharmacologic_class: 'ACE Inhibitors',
  va_class_name: 'CV800',
  des_schedule: 'N/A',
  duration_condition: 'Chronic',
  Strength: '20mg',
};

// ---------------------------------------------------------------------------
// Contract details — matches singleContractInterface[]
// ---------------------------------------------------------------------------

export const mockContractDetails: singleContractInterface[] = [
  {
    GFC: 54321,
    ContractNumber: 'SPE2D1-25-D-0077',
    ContractType: 'VA FSS',
    StartDate: '2024-11-01',
    EndDate: '2029-10-31',
  },
];

// ---------------------------------------------------------------------------
// Notes mock data — matches notesInterface[]
// ---------------------------------------------------------------------------

export const mockNotesData: notesInterface[] = [
  {
    ID: 1,
    Notes: 'Constrained forecast reviewed and approved for Q4 2025.',
    CreatedBy: 'John Smith',
    CreatedDate: '2025-10-18T10:00:00Z',
  },
  {
    ID: 2,
    Notes: 'Extended forecast months (13-15) adjusted based on supply chain constraints and historical VA demand patterns.',
    CreatedBy: 'Jane Doe',
    CreatedDate: '2025-11-02T15:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Planning periods for "Choose Plan" Dropdown
// ---------------------------------------------------------------------------

export interface PlanningPeriod {
  label: string;
  value: string;
}

export const mockPlanningPeriods: PlanningPeriod[] = [
  { label: 'Oct 2024 - Sep 2025', value: '2024-10-01' },
  { label: 'Oct 2025 - Sep 2026', value: '2025-10-01' },
];

// ---------------------------------------------------------------------------
// NDC headings
// ---------------------------------------------------------------------------

export interface NDCHeading {
  ndc: string;
  packSize: string;
}

export const mockDirectNDCs: NDCHeading[] = [
  { ndc: '54321-1111-01', packSize: '30' },
  { ndc: '54321-1111-02', packSize: '90' },
];

export const mockIndirectNDCs: NDCHeading[] = [
  { ndc: '54321-2222-01', packSize: '30' },
];

// ---------------------------------------------------------------------------
// Forecast detail interfaces
// ---------------------------------------------------------------------------

export interface ForecastDetail {
  ForecastPeriod: string;
  UnconstrainedForecast: number;
  ConstrainedForecast: number;
}

export interface NDCForecast {
  NDC11: string;
  SellingCategory: string;
  forecast_details: ForecastDetail[];
}

// ---------------------------------------------------------------------------
// Deterministic jitter helpers
// ---------------------------------------------------------------------------

const jitter = (base: number, idx: number, seed: number): number => {
  const noise = Math.sin(idx * 13.37 + seed * 7.91) * 0.08;
  return Math.round(base * (1 + noise));
};

// ---------------------------------------------------------------------------
// Forecast data: 3 NDCs (2 Direct, 1 Indirect), 15 months each
// ---------------------------------------------------------------------------

const buildForecastDetails = (
  baseUnconstrained: number,
  baseConstrained: number,
  seed: number,
): ForecastDetail[] =>
  MONTHS_15.map((m, idx) => ({
    ForecastPeriod: m.dateString,
    UnconstrainedForecast: jitter(baseUnconstrained, idx, seed),
    ConstrainedForecast: jitter(baseConstrained, idx, seed + 50),
  }));

export const mockForecastData: NDCForecast[] = [
  {
    NDC11: '54321-1111-01',
    SellingCategory: 'Direct',
    forecast_details: buildForecastDetails(12500, 11800, 1),
  },
  {
    NDC11: '54321-1111-02',
    SellingCategory: 'Direct',
    forecast_details: buildForecastDetails(8200, 7900, 2),
  },
  {
    NDC11: '54321-2222-01',
    SellingCategory: 'Indirect',
    forecast_details: buildForecastDetails(5400, 5100, 3),
  },
];

// ---------------------------------------------------------------------------
// Invoice sales summary
// ---------------------------------------------------------------------------

export interface InvoiceSales {
  NDC11: string;
  YTDAverage: number;
  ThreeMonthAverage: number;
  LastMonthAverage: number;
}

export const mockInvoiceSales: InvoiceSales[] = [
  { NDC11: '54321-1111-01', YTDAverage: 11950, ThreeMonthAverage: 12340, LastMonthAverage: 12580 },
  { NDC11: '54321-1111-02', YTDAverage: 7620, ThreeMonthAverage: 7880, LastMonthAverage: 8010 },
  { NDC11: '54321-2222-01', YTDAverage: 4870, ThreeMonthAverage: 5120, LastMonthAverage: 5250 },
];
